import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { avance, ETIQUETA_KPI, ICONO_KPI, KPIS, llegoALaMeta, TINTE_KPI, type Conteos, type Metas } from '../lib/kpi';

type MiKpi = { day: string; counts: Conteos; targets: Metas };

/**
 * EL KPI DEL DÍA, ABAJO DEL LOGO (pedido del dueño 2026-10-28): "llamadas out, llamadas in, emails y
 * notas que dejaron en el CRM… chiquito, decente, bonito, donde cada uno vaya viendo eso".
 *
 * Es lo de HOY de quien mira (su jornada). Se refresca cada 2 minutos y al volver a la pestaña, como
 * los contadores del menú; con la pestaña oculta no consulta. Las llamadas llegan con el call-log de
 * RingCentral, que publica la llamada al TERMINAR: una llamada recién cortada tarda unos minutos en sumar.
 * Si no cargó, no se pinta nada: un 0 inventado le diría a la persona que no hizo nada.
 */
export function KpiDelDia({ linkTo }: { linkTo?: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<MiKpi | null>(null);

  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      if (document.hidden) return;
      api.get<MiKpi>('/api/kpi/me').then((d) => { if (vivo) setData(d); }).catch(() => { /* se queda lo último que cargó */ });
    };
    cargar();
    const iv = window.setInterval(cargar, 120_000);
    document.addEventListener('visibilitychange', cargar);
    window.addEventListener('focus', cargar);
    return () => {
      vivo = false;
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', cargar);
      window.removeEventListener('focus', cargar);
    };
  }, []);

  if (!data) return null;
  const conMeta = KPIS.filter((k) => llegoALaMeta(0, data.targets[k]) != null);
  const todas = conMeta.length > 0 && conMeta.every((k) => llegoALaMeta(data.counts[k], data.targets[k]));

  const cuerpo = (
    <>
      <div className="kpi-strip-head">
        <span>{t('kpi.today')}</span>
        {todas && <span className="kpi-strip-done"><Check size={11} strokeWidth={2.6} /> {t('kpi.goalsMet')}</span>}
      </div>
      <div className="kpi-strip-row">
        {KPIS.map((k) => {
          const Icono = ICONO_KPI[k];
          const n = data.counts[k];
          const meta = data.targets[k];
          const llego = llegoALaMeta(n, meta);
          const base = meta != null && meta > 0
            ? t('kpi.tipWithGoal', { label: t(ETIQUETA_KPI[k]), n, goal: meta })
            : t('kpi.tip', { label: t(ETIQUETA_KPI[k]), n });
          // Las llamadas entre compañeros (extensión a extensión) no cuentan: se dice en el tooltip para
          // que nadie las espere en el contador (Erika, 2026-10-29).
          const titulo = k === 'calls_out' || k === 'calls_in' ? `${base} · ${t('kpi.tipExternalOnly')}` : base;
          return (
            <div key={k} className={`kpi-cell${llego ? ' is-met' : ''}`} title={titulo} aria-label={titulo}>
              <Icono size={14} strokeWidth={2} style={{ color: llego ? '#34d399' : TINTE_KPI[k] }} aria-hidden />
              <span className="kpi-num">
                <b>{n}</b>
                {meta != null && meta > 0 && <span className="kpi-goal">/{meta}</span>}
              </span>
              {meta != null && meta > 0 && (
                <span className="kpi-bar" aria-hidden>
                  <i style={{ width: `${avance(n, meta)}%`, background: llego ? '#34d399' : TINTE_KPI[k] }} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  return linkTo
    ? <Link to={linkTo} className="kpi-strip kpi-strip-link" aria-label={t('kpi.today')}>{cuerpo}</Link>
    : <div className="kpi-strip" role="group" aria-label={t('kpi.today')}>{cuerpo}</div>;
}
